import {
  Button,
  Card,
  Dropdown,
  Flex,
  Input,
  MenuProps,
  Modal,
  Space,
  Spin,
  Switch,
  Table,
  TableColumnsType,
  Tooltip,
  Typography,
  message
} from "antd";
import { ItemType } from "antd/es/menu/interface";
import { DeviceActionType } from "api/services/models/notification.types";
import { TAccountRolePrivileges, TCiamUser, TUser, UserStatus } from "api/services/models/types";
import { triggerDeviceAction } from "api/services/NotificationService";
import {
  getAllUsers,
  getUserLogs,
  toggleUserStatus,
  unregisterUser,
  getAllRoles
} from "api/services/UserService";
import { useTDAuthentication } from "auth/AuthenticationProvider";
import { AxiosError } from "axios";
import ErrorViewer from "components/ErrorViewer";
import { ReactComponent as AddUserIcon } from "Icons/vuesax/add-square.svg";
import { ReactComponent as UserConnectIcon } from "Icons/vuesax/cloud-connection.svg";
import { ReactComponent as UserDisconnectIcon } from "Icons/vuesax/cloud-remove.svg";
import { ReactComponent as UploadPendingDictation } from "Icons/vuesax/document-upload.svg";
import { ReactComponent as DocumentUpload } from "Icons/vuesax/export.svg";
import { ReactComponent as ImportIcon } from "Icons/vuesax/import.svg";
import { ReactComponent as InfoIcon } from "Icons/vuesax/info-circle.svg";
import { ReactComponent as ChangePasswordIcon } from "Icons/vuesax/key.svg";
import { ReactComponent as MoreCircleIcon } from "Icons/vuesax/more-circle.svg";
import { ReactComponent as PendingIcon } from "Icons/vuesax/timer-1.svg";
import { ReactComponent as UserEditIcon } from "Icons/vuesax/user-edit.svg";
import { ReactComponent as UserDisableIcon } from "Icons/vuesax/user-minus.svg";
import { ReactComponent as UserEnableIcon } from "Icons/vuesax/user-tick.svg";
import { useCallback, useEffect, useState } from "react";
import AddOrUpdateUser from "./AddOrUpdateUser";
import ChangePassword from "./ChangePassword";
import RegisterUser from "./RegisterUser";
import DictationIdForm from "./DictationIdForm";

type TUserActions = {
  onEnableOrDisableUser: (user: TUser) => void;
  onEditUser: (user: TUser) => void;
  onInviteUser: (user: TUser) => void;
  onUnregisterUser: (user: TUser) => void;
  onChangePassword: (user: TUser) => void;
  getUserLogs: (user: TUser) => void;
  onUploadUserLog: (user: TUser) => void;
  getPendingDictationCount: (user: TUser) => void;
  onTriggerUploadPending: (user: TUser) => void;
  onTriggerUploadDictationById: (user: TUser) => void;
};

const UserRolesCell = (roles?: string[]) => {
  const mergedRoles = roles?.join(", ") ?? "";
  return (
    <span
      title={mergedRoles}
      style={{
        display: "inline-block",
        maxWidth: 200,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis"
      }}
    >
      {mergedRoles}
    </span>
  );
};

const UserRegistrationStatusCell = (ciamUser: TCiamUser | null | undefined, record: TUser) => {
  const isExternalUser = ciamUser?.creationType === "Invitation";
  if (ciamUser == null) {
    return <span style={{ whiteSpace: "nowrap" }}>Not Registered</span>;
  }
  if (record.userStatus === UserStatus.Inactive) {
    return (
      <span style={{ whiteSpace: "nowrap", color: "red" }}>
        {isExternalUser ? "External User" : "Local User"}
        {isExternalUser && ciamUser.externalUserState ? ` (${ciamUser.externalUserState})` : ""}
      </span>
    );
  }
  return (
    <span style={{ whiteSpace: "nowrap", color: "#1868db" }}>
      {isExternalUser ? "External User" : "Local User"}
      {isExternalUser && ciamUser.externalUserState ? ` (${ciamUser.externalUserState})` : ""}
    </span>
  );
};

const buildActionsMenu = (user: TUser, actions: TUserActions): MenuProps => {
  const { logoutUser, webDUser } = useTDAuthentication();
  if (user.userStatus !== UserStatus.Active) {
    return {
      items: [
        {
          key: "enableUser",
          icon: <UserEnableIcon className="icon" />,
          onClick: () => actions.onEnableOrDisableUser(user),
          label: "Enable User",
          className: "user-context-menu"
        }
      ]
    };
  }
  const items: ItemType[] = [
    ...(user.ciamEmail
      ? [
          {
            key: "entraID",
            label: `Entra ID - ${user.ciamEmail}`,
            className: "ciam-user-context-menu"
          }
        ]
      : []),
    ...(webDUser?.isInternalUser()
      ? [
          {
            key: "getUserLogs",
            icon: <ImportIcon className="icon" />,
            onClick: () => actions.getUserLogs(user),
            label: (
              <div className="info-label">
                <span>Open Log</span>
                <Tooltip
                  title="Open user logs for VScribe+"
                  color="#fff"
                  styles={{ body: { color: "#4f4f4fff" } }}>
                  <InfoIcon className="info-icon" />
                </Tooltip>
              </div>
            ),
            className: "user-context-menu"
          }
        ]
      : []),
    {
      key: "uploadUserLog",
      icon: <DocumentUpload className="icon" />,
      onClick: () => actions.onUploadUserLog(user),
      label: (
        <div className="info-label">
          <span>Retrive Logs</span>
          <Tooltip
            title="Retrive user logs form VScribe+ Devices"
            color="#fff"
            styles={{ body: { color: "#4f4f4fff" } }}>
            <InfoIcon className="info-icon" />
          </Tooltip>
        </div>
      ),
      className: "user-context-menu"
    },
    {
      key: "getPendingDictationCount",
      icon: <PendingIcon className="icon" />,
      onClick: () => actions.getPendingDictationCount(user),
      label: (
        <div className="info-label">
          <span>Pending Dictations</span>
          <Tooltip
            title="Retrieve current pending dictation count for the user"
            color="#fff"
            styles={{ body: { color: "#4f4f4fff" } }}>
            <InfoIcon className="info-icon" />
          </Tooltip>
        </div>
      ),
      className: "user-context-menu"
    },
    {
      key: "triggerUploadPending",
      icon: <UploadPendingDictation className="icon" />,
      onClick: () => actions.onTriggerUploadPending(user),
      label: (
        <div className="info-label">
          <span>Upload Dictations</span>
          <Tooltip
            title="Upload pending dictations for the user"
            color="#fff"
            styles={{ body: { color: "#4f4f4fff" } }}>
            <InfoIcon className="info-icon" />
          </Tooltip>
        </div>
      ),
      className: "user-context-menu"
    },
    {
      key: "triggerUploadDictationById",
      icon: <UploadPendingDictation className="icon" />,
      onClick: () => actions.onTriggerUploadDictationById(user),
      label: (
        <div className="info-label">
          <span>Upload Dictation by Id</span>
          <Tooltip
            title="Upload pending dictation by Id for the user"
            color="#fff"
            styles={{ body: { color: "#4f4f4fff" } }}>
            <InfoIcon className="info-icon" />
          </Tooltip>
        </div>
      ),
      className: "user-context-menu"
    },
    {
      key: "editUser",
      icon: <UserEditIcon className="icon" />,
      onClick: () => actions.onEditUser(user),
      label: "Edit User",
      className: "user-context-menu"
    },
    {
      key: "disableUser",
      icon: <UserDisableIcon className="icon" />,
      onClick: () => actions.onEnableOrDisableUser(user),
      label: "Disable User",
      className: "user-context-menu"
    },
    {
      key: "registerUser",
      icon:
        user.ciamUserId != null ? (
          <UserDisconnectIcon className="icon" />
        ) : (
          <UserConnectIcon className="icon" />
        ),
      onClick: () =>
        user.ciamUserId != null ? actions.onUnregisterUser(user) : actions.onInviteUser(user),
      label: user.ciamUserId != null ? "Unregister" : "Register",
      className: "user-context-menu"
    }
  ];

  if (user.ciamUser != null && user.ciamUser?.creationType !== "Invitation") {
    items.push({
      key: "changePassword",
      icon: <ChangePasswordIcon className="icon" />,
      onClick: () => actions.onChangePassword(user),
      label: "Change password",
      className: "user-context-menu"
    });
  }
  return { items };
};

const composeColumns = (users: TUser[], roleList: TAccountRolePrivileges | undefined, actions: TUserActions): TableColumnsType<TUser> => {
  return [
    {
      title: "User Id",
      dataIndex: "userId",
      key: "userId",
      className: "tranform-caps",
      sorter: (s1, s2) => s1.userId.localeCompare(s2.userId),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
        <div style={{ padding: 8 }} onKeyDown={e => e.stopPropagation()} role="none">
          <Input
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            style={{ marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button
              onClick={() => clearFilters?.({ closeDropdown: true, confirm: true })}
              size="small"
              style={{ width: 90 }}>
              Reset
            </Button>
            <Button
              type="primary"
              onClick={() => confirm({ closeDropdown: true })}
              size="small"
              style={{ width: 90 }}>
              Search
            </Button>
          </Space>
        </div>
      ),
      filterDropdownProps: {
        placement: "bottom",
      },
      filterSearch: true,
      onFilter: (value, user) =>
        user.userId.toLowerCase().includes((value as string).toLowerCase())
    },
    {
      title: "First Name",
      dataIndex: "firstName",
      key: "firstName",
      sorter: (s1, s2) => s1.firstName.localeCompare(s2.firstName)
    },
    {
      title: "Middle Initial",
      dataIndex: "middleInitial",
      key: "middleInitial",
      width: "100px",
      sorter: (s1, s2) => s1.middleInitial?.localeCompare(s2.middleInitial)
    },
    {
      title: "Last Name",
      dataIndex: "lastName",
      key: "lastName",
      sorter: (s1, s2) => s1.lastName.localeCompare(s2.lastName),
      filters: [
        ...new Set(users.map(u => u.lastName).toSorted((n1, n2) => n1.localeCompare(n2)))
      ].map(lastName => ({
        text: lastName,
        value: lastName
      })),
      filterDropdownProps: {
        placement: "bottom",
      },
      onFilter: (value, user) => user.lastName === value
    },
    {
      title: "Primary Location",
      dataIndex: ["primaryLocation", "locationName"],
      width: "200px",
      sorter: (s1, s2) =>
        s1.primaryLocation?.locationName.localeCompare(s2.primaryLocation?.locationName),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }} onKeyDown={e => e.stopPropagation()} role="none">
          <Input
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            style={{ marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button
              onClick={() => clearFilters?.({ closeDropdown: true, confirm: true })}
              size="small"
              style={{ width: 90 }}>
              Reset
            </Button>
            <Button
              type="primary"
              onClick={() => confirm({ closeDropdown: true })}
              size="small"
              style={{ width: 90 }}>
              Search
            </Button>
          </Space>
        </div>
      ),
      filterDropdownProps: {
        placement: "bottom",
      },
      filterSearch: true,
      onFilter: (value, user) =>
        user.primaryLocation?.locationName.toLowerCase().includes((value as string).toLowerCase())
    },
    {
      title: "Roles",
      dataIndex: "authorizedRoles",
      key: "roles",
      render: UserRolesCell,
      filters: [
        ...new Set(
          roleList?.roles?.flatMap(role => role.description) ?? []
        )
      ]
        .sort((a, b) => a.localeCompare(b))
        .map(roles => ({
          text: roles,
          value: roles,
        })),
      filterSearch: true,
      filterMultiple: true,
      onFilter: (value, user) => { return user.authorizedRoles?.includes(value as string) ?? false; }
    },
    {
      title: "Registration Status",
      key: "RegistrationStatus",
      dataIndex: "ciamUser",
      render: UserRegistrationStatusCell,
      filters: [
        {
          text: "Not Registered",
          value: "not-registered"
        },
        {
          text: "External User (Accepted)",
          value: "external-accepted"
        },
        {
          text: "External User (Pending Acceptance)",
          value: "external-pending"
        },
        {
          text: "Local User",
          value: "local"
        }
      ],
      filterDropdownProps: {
        placement: "bottom",
      },
      onFilter: (value, user) => {
        const ciamUser = user.ciamUser;

        if (value === "not-registered") {
          return ciamUser == null;
        }

        if (value === "external-accepted") {
          return ciamUser?.creationType === "Invitation" && ciamUser.externalUserState === "Accepted";
        }

        if (value === "external-pending") {
          return ciamUser?.creationType === "Invitation" && ciamUser.externalUserState === "PendingAcceptance";
        }

        if (value === "local") {
          return ciamUser != null && ciamUser.creationType !== "Invitation";
        }

        return false;
      }
    },
    {
      title: "Actions",
      sorter: false,
      key: "actions",
      width: "50px",
      render: (_, r) => (
        <Flex justify="center" align="center">
          <Dropdown
            menu={buildActionsMenu(r, actions)}
            trigger={["click", "contextMenu"]}
            placement="bottomLeft"
            className="user-context-menu">
            <MoreCircleIcon
              width="16px"
              height="16px"
              style={{ cursor: "pointer", textAlign: "center" }}
            />
          </Dropdown>
        </Flex>
      )
    }
  ];
};

type TModalUserState = {
  user?: TUser;
  showModal: boolean;
};

const ManageUsers = () => {
  const { accountId } = useTDAuthentication();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | AxiosError | null>();
  const [users, setUsers] = useState<TUser[]>([]);
  const [editUserState, setEditUserState] = useState<TModalUserState>({ showModal: false });
  const [registerUserState, setRegisterUserState] = useState<TModalUserState>({ showModal: false });
  const [unregisterUserState, setUnregisterUserState] = useState<TModalUserState>({ showModal: false });
  const [changeUserPasswordState, setChangeUserPasswordState] = useState<TModalUserState>({ showModal: false });
  const [dictationIdState, setDictationIdState] = useState<TModalUserState>({ showModal: false });

  const [tableKey, setTableKey] = useState(1000);
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [includeDisabledUsers, setIncludeDisabledUsers] = useState(false);
  const [roleList, setRoleList] = useState<TAccountRolePrivileges>();
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);

  const filteredRows = users.filter(user => {
    if (!includeDisabledUsers && user.userStatus === UserStatus.Inactive) {
      return false;
    }

    if (
      selectedRoles.length > 0 &&
      !selectedRoles.every(role =>
        user.authorizedRoles?.includes(role)
      )
    ) {
      return false;
    }

    return true;
  });

  useEffect(() => {
  if (accountId) {
    setLoading(true);
    setError(null);

    Promise.all([
      getAllUsers(accountId),
      getAllRoles(accountId)
    ])
      .then(([usersData, rolesData]) => {
        setUsers(usersData);
        setRoleList(rolesData);
        setTableKey(k => k + 1);
      })
      .catch(setError)
      .finally(() => setLoading(false));
  }
}, [accountId]);

  const addOrUpdateUserToState = useCallback((newOrUpatedUser: TUser) => {
    setUsers(users => {
      const index = users.findIndex(u => u.userId === newOrUpatedUser.userId);
      if (index === -1) {
        users.push(newOrUpatedUser);
      } else {
        users.splice(index, 1, { ...newOrUpatedUser });
      }
      return [...users];
    });
  }, []);

  const enableOrDisableUser = useCallback((user: TUser) => {
    toggleUserStatus(user).then(addOrUpdateUserToState).catch(setError);
  }, []);

  const doEditUser = useCallback((user: TUser) => {
    setEditUserState({ showModal: true, user });
  }, []);

  const doRegisterUser = useCallback((user: TUser) => {
    setRegisterUserState({ showModal: true, user });
  }, []);

  const doUnregisterUser = useCallback((user: TUser) => {
    setUnregisterUserState({ showModal: true, user });
  }, []);

  const doChangeUserPassword = useCallback((user: TUser) => {
    setChangeUserPasswordState({ showModal: true, user });
  }, []);

  const doUploadDictationById = useCallback((user: TUser) => {
    setDictationIdState({ showModal: true, user });
  }, []);

  const getUserLog = async (user: TUser) => {
    try {
      setLoading(true);
      const { blob } = await getUserLogs(accountId!, user.userId).finally(() =>
        setLoading(false)
      );
      const url = window.URL.createObjectURL(new Blob([blob], { type: "text/plain" }));
      const newWindow = window.open(url, "_blank");
      if (!newWindow) {
        alert("Please allow popups for this website");
      } else {
        message.success("Logs fetched successfully!");
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 100);
      }
    } catch (err: any) {
      console.error(err);
      message.error(err.message);
      return;
    }
  };

  const onUploadUserLog = async (user: TUser) => {
    try {
      setLoading(true);
      const response = await triggerDeviceAction({
        accountId: accountId!,
        userId: user.userId,
        deviceActionType: DeviceActionType.UploadLogs
      }).finally(() => setLoading(false));
      console.log("response", response);
      message.info(response.data.message);
    } catch (err: any) {
      console.log(err);
      message.error(err.message);
      return;
    }
  };

  const getPendingDictationCount = async (user: TUser) => {
    try {
      setLoading(true);
      const response = await triggerDeviceAction({
        accountId: accountId!,
        userId: user.userId,
        deviceActionType: DeviceActionType.UploadPendingDictations
      }).finally(() => setLoading(false));
      console.log("response", response);
      message.info(response.data.message);
    } catch (err: any) {
      console.log(err);
      message.error(err.message);
      return;
    }
  };

  const onTriggerUploadPending = async (user: TUser) => {
    try {
      setLoading(true);
      const response = await triggerDeviceAction({
        accountId: accountId!,
        userId: user.userId,
        deviceActionType: DeviceActionType.UploadDictationData
      }).finally(() => setLoading(false));
      console.log("response", response);
      message.info(response.data.message);
    } catch (err: any) {
      console.log(err);
      message.error(err.message);
      return;
    }
  };

  const onEditUserSuccess = (user?: TUser) => {
    setEditUserState({ showModal: false });
    if (user) {
      addOrUpdateUserToState(user);
    }
  };

  const onRegisterUserSuccess = (user?: TUser) => {
    setRegisterUserState({ showModal: false });
    if (user) {
      addOrUpdateUserToState(user);
    }
  };

  const onUnregisterUserConfirmation = (user?: TUser) => {
    setUnregisterUserState({ showModal: false });
    if (user) {
      setLoading(true);
      setError(null);
      unregisterUser(user)
        .then(addOrUpdateUserToState)
        .catch(setError)
        .finally(() => setLoading(false));
    }
  };

  const onDictationIdFormClose = () => {
    setDictationIdState({ showModal: false });
  };

  return (
    <Card
      style={{
        padding: "16px"
      }}
      className="default-border"
      styles={{ body: { padding: "0" } }}>
      <Flex
        wrap
        gap="middle"
        align="center"
        justify="space-between">   
        <Flex align="center" gap={20}>
          <Typography.Title style={{ fontSize: "1rem", marginBottom: "13px" }}>
            Manage Users
          </Typography.Title>
        </Flex>
        <Flex align="center" gap={30}>
          <Flex align="center" gap={8}>
            <Typography>
              Include disabled users
            </Typography>
            <Switch
              id="include-disabled-users"
              checked={includeDisabledUsers}
              onChange={(checked) => setIncludeDisabledUsers(checked)}
              size="small"
            />
          </Flex>
          <Button
            type="primary"
            size="middle"
            className="default-button primary-btn"
            icon={<AddUserIcon className="default-icon" />}
            iconPosition={"start"}
            title="Add New User"
            onClick={() => setEditUserState({ showModal: true })}>
            <span>Add New</span>
          </Button>
        </Flex>
      </Flex>
      <Flex justify="end">
        <Typography.Text style={{ fontSize: ".75rem" }}>
          {`Total Users: ${includeDisabledUsers ? users.length : users.filter(u => u.userStatus === UserStatus.Active).length}`}
        </Typography.Text>
      </Flex>
      
      {error && <ErrorViewer error={error} />}
      <Card className="card-style" styles={{ body: { padding: 0 } }}>
          <Table<TUser>
            key={`Key:${tableKey}`}
            loading={loading}
            columns={composeColumns(users, roleList, {
              onEnableOrDisableUser: enableOrDisableUser,
              onEditUser: doEditUser,
              onInviteUser: doRegisterUser,
              onUnregisterUser: doUnregisterUser,
              onChangePassword: doChangeUserPassword,
              getUserLogs: getUserLog,
              onUploadUserLog: onUploadUserLog,
              getPendingDictationCount: getPendingDictationCount,
              onTriggerUploadPending: onTriggerUploadPending,
              onTriggerUploadDictationById: doUploadDictationById
            })}
            dataSource={filteredRows.sort((a, b) => a.userId.localeCompare(b.userId))}
            onChange={(_, filters) => {
              setSelectedRoles((filters.roles as string[]) || []);
            }}
            pagination={{
              pageSize: pageSize,
              showSizeChanger: {
                showSearch: false
              },
              pageSizeOptions: [5, 10, 20, 50, 100],
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size || 10);
              }
            }}
            rowKey={"userId"}
            onRow={r => ({
              style: {
                color: r.userStatus === UserStatus.Inactive ? "red" : "inherit",
              }
            })}
            className="table-style"
            components={{
              body: {
                cell: (props: any) => (
                  <td
                    {...props}
                    style={{
                      borderBottomWidth: "1px",
                      borderBottomColor: "#f0f0f0",
                      borderBottomStyle: "solid",
                      fontWeight: "500"
                    }}
                  />
                )
              }
            }}
            style={{ border: "none" }}
          />
        </Card>

      {editUserState.showModal && (
        <AddOrUpdateUser onClose={onEditUserSuccess} user={editUserState.user} />
      )}

      {registerUserState.showModal && (
        <RegisterUser onClose={onRegisterUserSuccess} user={registerUserState.user!} />
      )}

      {unregisterUserState.showModal && unregisterUserState.user && (
        <Modal
          title={`Unregister user`}
          open={unregisterUserState.showModal}
          onOk={() => onUnregisterUserConfirmation(unregisterUserState.user)}
          onCancel={() => setUnregisterUserState({ showModal: false })}>
          {`Are you sure you want to unregister user: ${unregisterUserState.user?.userId}`}
        </Modal>
      )}

      {changeUserPasswordState.showModal && changeUserPasswordState.user != null && (
        <ChangePassword
          user={changeUserPasswordState.user}
          onClose={() => setChangeUserPasswordState({ showModal: false })}></ChangePassword>
      )}

      {dictationIdState.showModal && dictationIdState.user != null && (
        <DictationIdForm user={dictationIdState.user} onClose={onDictationIdFormClose} />
      )}
    </Card>
  );
};

export default ManageUsers;
